import { defineMessages, enPlural, ruPlural } from '@/i18n'

/** Тексты вкладок и секций настроек курса. */
export const messages = defineMessages({
  ru: {
    // tabs.ts
    tabBasicLabel: 'Основное',
    tabBasicTitle: 'Основная информация',
    tabBasicDescription: 'Название, описание и баннер',
    tabAccessLabel: 'Доступ',
    tabAccessTitle: 'Доступ',
    tabAccessDescription: 'Кто видит курс и в каком разделе каталога он лежит',
    tabDesignLabel: 'Дизайн-система',
    tabDesignTitle: 'Дизайн-система',
    tabDesignDescription: 'Стиль и образцы для картинок курса',
    tabPricingLabel: 'Монетизация',
    tabPricingTitle: 'Монетизация',
    tabPricingDescription: 'Цены, подписка и триал-доступ',
    tabMapLabel: 'Карта',
    tabMapTitle: 'Карта курса',
    tabMapDescription: 'Персонажи, которые оживляют карту уроков',
    tabLanguagesLabel: 'Язык',
    tabLanguagesTitle: 'Язык',
    tabLanguagesDescription: 'Базовый язык курса и языки перевода',
    tabCertificatesLabel: 'Сертификаты',
    tabCertificatesTitle: 'Сертификаты',
    tabCertificatesDescription: 'Сертификат о прохождении курса',

    // общие
    noRights: 'Нет прав',

    // BasicInfoSection
    translationLockNote: 'Идёт перевод — текст временно недоступен для правки',
    courseName: 'Название курса',
    description: 'Описание',
    courseBanner: 'Баннер курса',

    // BaseLangHint
    baseLangHint: 'Базовый язык — это тот язык, с которого идёт автоматический перевод через AI.',

    // CourseSettingsPage
    soon: 'скоро',
    backToMyCourses: 'К моим курсам',
    backToEditor: 'К редактору',
    courseNotFound: 'Курс не найден',
    loadFailedTitle: 'Ой, курс не открылся…',
    loadFailedDescription: 'Проверьте интернет и попробуйте ещё раз.',
    retry: 'Повторить',

    // CourseAccessBlock
    unfilledBase: 'Заполните название и описание курса, чтобы его опубликовать',
    unfilledSecond: (lang: string) =>
      `Заполните название и описание на языке ${lang}, чтобы опубликовать курс`,

    // CertificatesSection
    certificatesTitle: 'Ой, сертификаты пока недоступны…',
    certificatesDescription:
      'Сертификат о прохождении курса появится позже. Пока здесь ничего не настроить.',

    // MapSection
    slotUpload: (n: number) => `Загрузить маскота в слот ${n}`,
    slotReplace: (n: number) => `Заменить маскота в слоте ${n}`,
    slotRemove: (n: number) => `Удалить маскота из слота ${n}`,
    slotFile: (n: number) => `Файл маскота для слота ${n}`,
    slotOpensAt: (need: number) =>
      `Откроется, когда в курсе будет ${need} ${ruPlural(need, 'урок', 'урока', 'уроков')}`,
    slotFillPrevious: 'Сначала заполните предыдущее место',
    slotFromLessons: (need: number) => `С ${need} ${ruPlural(need, 'урока', 'уроков', 'уроков')}`,
    mascotUnlockFirst: (need: number) =>
      `Загрузка маскотов станет доступна, когда в курсе будет ${need} ${ruPlural(need, 'урок', 'урока', 'уроков')}`,
    mapMascotsTitle: 'Маскоты на карте курса',
    mascotTransparentHint:
      'Загружайте PNG или WebP с прозрачным фоном — фон картинки будет виден на карте.',

    // MascotSlot
    mascot: 'Маскот',
    replaceMascot: 'Заменить маскота',
    removeMascot: 'Удалить маскота',

    // LanguageCard
    baseBadge: 'Базовый',
    makeBase: 'Сделать базовым',
    addLanguage: 'Добавить язык',
    visibilityOn: 'Виден ученикам',
    visibilityOff: 'Скрыт от учеников',
    langActionsAria: (name: string) => `Действия: ${name}`,
    deleteLang: 'Удалить язык',
    makeBaseNeedsVisible: 'Сначала включите видимость языка',
    actionsDuringRun: 'Недоступно, пока идёт генерация или перевод',
    langEnabled: 'Язык включён',
    langDisabled: 'Язык выключен',
    langSwitchAria: (name: string) => `Язык ${name}`,

    // PricingSection
    faq: 'Частые вопросы',

    // PriceInputs
    priceRub: 'Цена в рублях',
    priceUsd: 'Цена в долларах',
    priceRaised: (min: string) => `Цена ниже минимума — поставили ${min}`,
    priceForeverTitle: 'Цена покупки навсегда',

    // SalesModelPicker
    salesModel: 'Модель продажи',
    salesSubscription: 'В подписке',
    salesStandalone: 'Отдельно',
    salesFree: 'Бесплатный',
    salesFreeOnlyTitle: 'Экспериментальный доступ',
    salesFreeOnly:
      'С экспериментальным доступом курс может быть только бесплатным. Платные курсы и курсы в подписке станут доступны вместе с полным доступом.',

    // SalesModelInfoPanel
    infoSubscription:
      'Курсы в подписке оплачиваются по стриминговой модели: 40% наших доходов с подписок ' +
      'после вычета налогов, комиссий магазинов приложений и эквайринга ежемесячно ' +
      'распределяется между ' +
      'авторами пропорционально просмотрам их курсов.',
    infoFree: 'Курс полностью бесплатный — доступен всем без оплаты и подписки.',

    // RoadmapPreview
    start: 'Начать',

    // DesignSystemSection
    imageStyle: 'Стиль картинок курса',
    imageStyleHintLabel: 'Подсказка про стиль картинок',
    imageStyleHint:
      'Опишите словами, в каком стиле нейросеть должна рисовать картинки курса. Без стиля картинки не будут сгенерированы.',
    imageStylePlaceholder: 'Например: flat vector, soft pastel palette',
    styleRefs: 'Картинки-образцы стиля',
    styleRefsHintLabel: 'Подсказка про картинки-образцы',
    styleRefsHint:
      'Референсы стиля, маскотов и цветовой палитры. Их используют AI-модели, когда создают медиа и иллюстрации в вашем курсе.',

    // StyleReferencesGallery
    styleRefFile: 'Файл картинки-образца',
    styleRefRemove: (n: number) => `Удалить картинку-образец ${n}`,
    styleRefUpload: 'Загрузить картинку-образец',
    styleRefTitle: 'Картинка-образец',

    // TrialAccessRow
    trialAccess: 'Триал-доступ',
    trialImpossible: 'Для включения триала нужно минимум 2 урока',
    trialHint: (max: number) => `Бесплатные уроки до оплаты, не больше ${max}`,
    trialCount: 'Количество бесплатных уроков',
    trialOverLimit: (max: number) => `Не больше ${max}: хотя бы один урок останется платным`,
    trialTotal: (n: number) => `Всего уроков в курсе: ${n}. Хотя бы один останется платным.`,

    // TranslationLangRow
    visibility: 'Видимость',
    undeclaredDraft:
      'Перевод залит, но язык не объявлен. Включите переключатель — тогда курс появится на этом языке в каталоге, как только вы его опубликуете.',
    undeclaredLive:
      'Перевод залит, но язык не объявлен — в каталоге на этом языке курса нет. Включите переключатель, чтобы его увидели студенты.',
  },
  en: {
    tabBasicLabel: 'General',
    tabBasicTitle: 'General information',
    tabBasicDescription: 'Title, description and banner',
    tabAccessLabel: 'Access',
    tabAccessTitle: 'Access',
    tabAccessDescription: 'Who can see the course and which catalog section it belongs to',
    tabDesignLabel: 'Design system',
    tabDesignTitle: 'Design system',
    tabDesignDescription: 'Style and references for course images',
    tabPricingLabel: 'Monetization',
    tabPricingTitle: 'Monetization',
    tabPricingDescription: 'Prices, subscription and trial access',
    tabMapLabel: 'Map',
    tabMapTitle: 'Course map',
    tabMapDescription: 'Characters that bring the lesson map to life',
    tabLanguagesLabel: 'Language',
    tabLanguagesTitle: 'Language',
    tabLanguagesDescription: 'Source language and translation languages',
    tabCertificatesLabel: 'Certificates',
    tabCertificatesTitle: 'Certificates',
    tabCertificatesDescription: 'Course completion certificate',

    noRights: 'No permission',

    translationLockNote: 'Translation in progress — text is temporarily locked',
    courseName: 'Course title',
    description: 'Description',
    courseBanner: 'Course banner',

    baseLangHint: 'The source language is the one AI automatically translates from.',

    soon: 'Soon',
    backToMyCourses: 'Back to my courses',
    backToEditor: 'Back to the editor',
    courseNotFound: 'Course not found',
    loadFailedTitle: 'Oops, the course didn’t load…',
    loadFailedDescription: 'Check your connection and try again.',
    retry: 'Retry',

    unfilledBase: 'Fill in the course title and description to publish it',
    unfilledSecond: (lang: string) =>
      `Fill in the title and description in ${lang} to publish the course`,

    certificatesTitle: 'Oops, certificates aren’t available yet…',
    certificatesDescription:
      'Course completion certificates are coming later. There’s nothing to set up here yet.',

    slotUpload: (n: number) => `Upload mascot to slot ${n}`,
    slotReplace: (n: number) => `Replace mascot in slot ${n}`,
    slotRemove: (n: number) => `Remove mascot from slot ${n}`,
    slotFile: (n: number) => `Mascot file for slot ${n}`,
    slotOpensAt: (need: number) =>
      `Unlocks once the course has ${need} ${enPlural(need, 'lesson', 'lessons')}`,
    slotFillPrevious: 'Fill the previous slot first',
    slotFromLessons: (need: number) => `From ${need} ${enPlural(need, 'lesson', 'lessons')}`,
    mascotUnlockFirst: (need: number) =>
      `Mascot upload unlocks once the course has ${need} ${enPlural(need, 'lesson', 'lessons')}`,
    mapMascotsTitle: 'Mascots on the course map',
    mascotTransparentHint:
      'Upload a PNG or WebP with a transparent background — any background in the image shows on the map.',

    mascot: 'Mascot',
    replaceMascot: 'Replace mascot',
    removeMascot: 'Remove mascot',


    baseBadge: 'Source',
    makeBase: 'Set as source',
    addLanguage: 'Add language',
    visibilityOn: 'Visible to learners',
    visibilityOff: 'Hidden from learners',
    langActionsAria: (name: string) => `${name} actions`,
    deleteLang: 'Delete language',
    makeBaseNeedsVisible: 'Turn on language visibility first',
    actionsDuringRun: 'Unavailable while generation or translation is running',
    langEnabled: 'Language on',
    langDisabled: 'Language off',
    langSwitchAria: (name: string) => `Language ${name}`,

    faq: 'FAQ',

    priceRub: 'Price in rubles',
    priceUsd: 'Price in dollars',
    priceRaised: (min: string) => `Price was below the minimum — set to ${min}`,
    priceForeverTitle: 'Lifetime purchase price',

    salesModel: 'Sales model',
    salesSubscription: 'Subscription',
    salesStandalone: 'One-time purchase',
    salesFree: 'Free',
    salesFreeOnlyTitle: 'Experimental access',
    salesFreeOnly:
      'With experimental access, a course can only be free. Paid and subscription courses become available with full access.',

    infoSubscription:
      'Subscription courses are paid on a streaming model: each month, 40% of our subscription revenue ' +
      'after taxes, app store fees and payment processing fees is distributed among authors in proportion to views of their courses.',
    infoFree:
      'The course is completely free — available to everyone without payment or subscription.',

    start: 'Start',

    imageStyle: 'Course image style',
    imageStyleHintLabel: 'About image style',
    imageStyleHint:
      'Describe in words the style AI should use for course images. Without a style, images won’t be generated.',
    imageStylePlaceholder: 'For example: flat vector, soft pastel palette',
    styleRefs: 'Style reference images',
    styleRefsHintLabel: 'About reference images',
    styleRefsHint:
      'References for style, mascots and color palette. AI models use them when creating media and illustrations for your course.',

    styleRefFile: 'Reference image file',
    styleRefRemove: (n: number) => `Remove reference image ${n}`,
    styleRefUpload: 'Upload reference image',
    styleRefTitle: 'Reference image',

    trialAccess: 'Trial access',
    trialImpossible: 'Trial access needs at least 2 lessons',
    trialHint: (max: number) => `Free lessons before payment, up to ${max}`,
    trialCount: 'Number of free lessons',
    trialOverLimit: (max: number) => `No more than ${max}: at least one lesson stays paid`,
    trialTotal: (n: number) => `Lessons in the course: ${n}. At least one stays paid.`,

    visibility: 'Visibility',
    undeclaredDraft:
      'The translation is there, but the language isn’t turned on. Turn it on, and the course will appear in the catalog in this language once you publish it.',
    undeclaredLive:
      'The translation is there, but the language isn’t turned on — the course isn’t in the catalog in this language. Turn it on so learners can see it.',
  },
})
